import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../config";


export default function VolunteerSignup() {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    const formData = Object.fromEntries(new FormData(e.target).entries());

    try {
     const res = await fetch(`${API_BASE_URL}/api/volunteers`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(formData),
});

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Failed to submit");
      }

      setSent(true);
      e.target.reset();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      console.error(err);
    }
  }

  if (sent) {
  return (
    <div className="max-w-2xl mx-auto mt-10 rounded-2xl border shadow-soft overflow-hidden bg-white">
      {/* Softer professional pink header */}
      <div className="bg-brand-100 text-brand-800 px-6 py-5 border-b border-brand-200">
        <h1 className="text-xl font-semibold">Application Received 🎉</h1>
        <p className="text-brand-700/90 text-sm">
          Thank you for volunteering with the Women & Girls in STEM Club.
        </p>
      </div>

      <div className="p-6">
        <p className="text-ink/80">
          Your submission has been recorded successfully. We’ll follow up with you soon by email.
          You can return to the homepage or submit another response.
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={() => navigate("/")}
            className="rounded-xl bg-brand-600 text-white px-4 py-2 font-medium hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-600"
          >
            Back to Home
          </button>
          <button
            onClick={() => {
              setSent(false);
              navigate(location.pathname, { replace: true });
            }}
            className="rounded-xl border border-brand-300 text-brand-700 px-4 py-2 font-medium hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-400"
          >
            Submit Another Response
          </button>
        </div>
      </div>
    </div>
  );
}

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-2xl border shadow-soft mt-10">
      {/* Header */}
      <header className="rounded-t-2xl bg-brand-100 border-b px-6 py-6 text-center">
        <h1 className="text-3xl font-bold text-ink">Volunteers for the Women & Girls in STEM Club</h1>
        <p className="mt-2 text-ink/80 max-w-lg mx-auto">
          Please complete this form to register your interest.
        </p>
      </header>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-6 grid gap-4" aria-describedby="vol-desc">
        <p id="vol-desc" className="sr-only">Volunteer details, year group, availability, and facilitation preference.</p>

        {error && (
          <div role="alert" className="rounded-lg border border-red-300 bg-red-50 text-red-700 p-3">{error}</div>
        )}

        <Field id="fullName" label="First and Last Name *">
          <input id="fullName" name="fullName" required className="rounded-xl border px-3 py-2 w-full" />
        </Field>

        <Field id="email" label="Email *">
          <input id="email" name="email" type="email" required className="rounded-xl border px-3 py-2 w-full" />
        </Field>

        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium">Year Group *</legend>
          <div className="grid sm:grid-cols-2 gap-2">
            {["1st Year", "2nd Year", "3rd Year", "4th Year", "Other"].map((opt) => (
              <label key={opt} className="inline-flex items-center gap-2 text-sm">
                <input type="radio" name="yearGroup" value={opt} required className="h-4 w-4" /> {opt}
              </label>
            ))}
          </div>
        </fieldset>

        <Field id="availability" label="Availability *" hint="e.g., Tuesdays 5–8 PM, Thursdays 6–8 PM">
          <textarea id="availability" name="availability" rows={3} required className="rounded-xl border px-3 py-2 w-full" />
        </Field>

        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium">Would you like to facilitate a session?</legend>
          <div className="flex gap-4">
            {["Yes", "No"].map((opt) => (
              <label key={opt} className="inline-flex items-center gap-2 text-sm">
                <input type="radio" name="facilitate" value={opt} required className="h-4 w-4" /> {opt}
              </label>
            ))}
          </div>
        </fieldset>

        <Field id="sessionDetails" label="If yes, what would the session be and what date do you prefer to run this session?">
          <textarea id="sessionDetails" name="sessionDetails" rows={3} className="rounded-xl border px-3 py-2 w-full" />
        </Field>

        <Field id="additionalInfo" label="Any further comments?">
          <textarea id="additionalInfo" name="additionalInfo" rows={3} className="rounded-xl border px-3 py-2 w-full" />
        </Field>

        <button
          type="submit"
          className="mt-2 rounded-xl bg-brand-600 text-white font-medium px-4 py-2 hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-600"
        >
          Submit Application
        </button>
      </form>
    </div>
  );
}

function Field({ id, label, hint, children }) {
  return (
    <div className="grid gap-1">
      <label htmlFor={id} className="text-sm font-medium">{label}</label>
      {hint && <p className="text-xs text-ink/60">{hint}</p>}
      {children}
    </div>
  );
}
